/**
 * Face Management — Guided Multi-Pose Enrollment
 *
 * Provides two enrollment methods:
 * 1. Guided webcam capture (4 poses: center, left, right, up)
 * 2. Photo upload with face detection
 *
 * Also handles browsing, renaming, and deleting face records.
 */

import * as H from './dist/human.esm.js';
import * as indexDb from './indexdb.js';
import { renderStationBadge } from './station.js';

// ── Configuration ──

const humanConfig = {
  cacheSensitivity: 0.01,
  modelBasePath: './models',
  filter: { enabled: true, equalization: true },
  debug: false,
  face: {
    enabled: true,
    detector: { rotation: true, return: true, mask: false },
    description: { enabled: true },
    iris: { enabled: true },
    emotion: { enabled: false },
    antispoof: { enabled: true },
    liveness: { enabled: true },
  },
  body: { enabled: false },
  hand: { enabled: false },
  object: { enabled: false },
  gesture: { enabled: true },
};

const human = new H.Human(humanConfig);

// ── Enrollment Steps ──
interface PoseStep {
  label: string;
  instruction: string;
  yawMin: number;
  yawMax: number;
  pitchMin: number;
  pitchMax: number;
}

const POSE_STEPS: PoseStep[] = [
  { label: 'center', instruction: 'look straight at camera', yawMin: -10, yawMax: 10, pitchMin: -10, pitchMax: 10 },
  { label: 'left', instruction: 'turn head toward left side of screen', yawMin: 12, yawMax: 35, pitchMin: -20, pitchMax: 15 },
  { label: 'right', instruction: 'turn head toward right side of screen', yawMin: -35, yawMax: -12, pitchMin: -20, pitchMax: 15 },
  { label: 'up', instruction: 'tilt chin up slightly', yawMin: -20, yawMax: 20, pitchMin: -30, pitchMax: -8 },
];

const HOLD_TIME = 600; // ms to hold pose before capture

function playBeep() {
  const ctx = new AudioContext();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.frequency.value = 880;
  gain.gain.value = 0.08;
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start();
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
  osc.stop(ctx.currentTime + 0.15);
}

// ── State ──
let detectionActive = false;
let detectionThread: number | null = null;
let drawThread: number | null = null;
const capturedSteps: Set<number> = new Set(); // which pose indices are done
let holdingStep = -1; // which pose we're currently holding for
let holdStart = 0;
let capturedFace: H.FaceResult | null = null;
let uploadFace: H.FaceResult | null = null;

interface CapturedPose {
  embedding: number[];
  imageData: ImageData;
  yaw: number;
  pitch: number;
  roll: number;
  faceScore: number;
  real: number;
  live: number;
  age: number;
  gender: string;
  genderScore: number;
}
const capturedPoses: CapturedPose[] = [];

// ── Logs ──
interface LogEntry {
  t: number;
  step: string;
  yaw: number;
  pitch: number;
  roll: number;
  gaze: number;
  score: number;
  real: number;
  live: number;
  size: number;
  holding: boolean;
  holdMs: number;
}
const logEntries: LogEntry[] = [];

// ── DOM ──
const dom = {
  stationBadge: document.getElementById('stationBadge') as HTMLDivElement,
  faceGrid: document.getElementById('faceGrid') as HTMLDivElement,
  emptyState: document.getElementById('emptyState') as HTMLDivElement,
  deleteAllBtn: document.getElementById('deleteAllBtn') as HTMLButtonElement,
  methodWebcam: document.getElementById('methodWebcam') as HTMLDivElement,
  webcamPanel: document.getElementById('webcamPanel') as HTMLDivElement,
  captureVideo: document.getElementById('captureVideo') as HTMLVideoElement,
  captureCanvas: document.getElementById('captureCanvas') as HTMLCanvasElement,
  enrollSteps: document.getElementById('enrollSteps') as HTMLDivElement,
  poseIndicator: document.getElementById('poseIndicator') as HTMLDivElement,
  poseThumbs: document.getElementById('poseThumbs') as HTMLDivElement,
  validationStatus: document.getElementById('validationStatus') as HTMLDivElement,
  webcamStatus: document.getElementById('webcamStatus') as HTMLDivElement,
  webcamName: document.getElementById('webcamName') as HTMLInputElement,
  webcamSaveBtn: document.getElementById('webcamSaveBtn') as HTMLButtonElement,
  webcamCancelBtn: document.getElementById('webcamCancelBtn') as HTMLButtonElement,
  retakeBtn: document.getElementById('retakeBtn') as HTMLButtonElement,
  methodUpload: document.getElementById('methodUpload') as HTMLDivElement,
  uploadPanel: document.getElementById('uploadPanel') as HTMLDivElement,
  uploadArea: document.getElementById('uploadArea') as HTMLDivElement,
  uploadImage: document.getElementById('uploadImage') as HTMLImageElement,
  uploadCanvas: document.getElementById('uploadCanvas') as HTMLCanvasElement,
  uploadValidation: document.getElementById('uploadValidation') as HTMLDivElement,
  uploadStatus: document.getElementById('uploadStatus') as HTMLDivElement,
  uploadName: document.getElementById('uploadName') as HTMLInputElement,
  uploadSaveBtn: document.getElementById('uploadSaveBtn') as HTMLButtonElement,
  uploadCancelBtn: document.getElementById('uploadCancelBtn') as HTMLButtonElement,
  fileInput: document.getElementById('fileInput') as HTMLInputElement,
  toast: document.getElementById('toast') as HTMLDivElement,
  loadingOverlay: document.getElementById('loadingOverlay') as HTMLDivElement,
  confirmOverlay: document.getElementById('confirmOverlay') as HTMLDivElement,
  confirmDeleteAll: document.getElementById('confirmDeleteAll') as HTMLButtonElement,
  confirmCancel: document.getElementById('confirmCancel') as HTMLButtonElement,
};

// ── Toast ──
let toastTimer: any = null;
function showToast(msg: string) {
  dom.toast.innerText = msg;
  dom.toast.classList.add('show');
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => dom.toast.classList.remove('show'), 2500);
}

// ── Face Grid ──
async function refreshGrid() {
  const faces = await indexDb.load();
  dom.faceGrid.innerHTML = '';
  if (faces.length === 0) { dom.emptyState.style.display = 'block'; dom.deleteAllBtn.style.display = 'none'; return; }
  dom.emptyState.style.display = 'none';
  dom.deleteAllBtn.style.display = 'inline-block';

  // Group faces by name
  const grouped = new Map<string, indexDb.FaceRecord[]>();
  for (const face of faces) {
    const name = face.name || 'Unnamed';
    if (!grouped.has(name)) grouped.set(name, []);
    grouped.get(name)!.push(face);
  }

  for (const [name, records] of grouped) {
    const card = document.createElement('div');
    card.className = 'face-card';

    // Show first pose as thumbnail
    const canvas = document.createElement('canvas');
    canvas.width = records[0].image.width;
    canvas.height = records[0].image.height;
    canvas.getContext('2d')?.putImageData(records[0].image, 0, 0);

    const nameDiv = document.createElement('div');
    nameDiv.className = 'face-card-name';
    nameDiv.innerText = name;
    nameDiv.contentEditable = 'true'; nameDiv.spellcheck = false;
    nameDiv.addEventListener('blur', async () => {
      const n = nameDiv.innerText.trim();
      if (n && n !== name) {
        for (const r of records) { r.name = n; await indexDb.update(r); }
        showToast(`renamed to "${n}"`);
        await refreshGrid();
      }
    });
    nameDiv.addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); nameDiv.blur(); } });

    const countDiv = document.createElement('div');
    countDiv.className = 'face-card-id';
    countDiv.innerText = `${records.length} pose${records.length > 1 ? 's' : ''}`;

    const delBtn = document.createElement('button');
    delBtn.className = 'delete-x'; delBtn.innerText = '×';
    delBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      for (const r of records) await indexDb.remove(r);
      showToast(`deleted "${name}" (${records.length} poses)`);
      await refreshGrid();
    });

    // ── Pose Gallery (expandable) ──
    const gallery = document.createElement('div');
    gallery.className = 'pose-gallery';

    const poseGrid = document.createElement('div');
    poseGrid.className = 'pose-grid';

    for (const rec of records) {
      const poseItem = document.createElement('div');
      poseItem.className = 'pose-item';

      const poseCanvas = document.createElement('canvas');
      poseCanvas.width = rec.image.width;
      poseCanvas.height = rec.image.height;
      poseCanvas.getContext('2d')?.putImageData(rec.image, 0, 0);

      const poseDelBtn = document.createElement('button');
      poseDelBtn.className = 'pose-delete';
      poseDelBtn.innerText = '×';
      poseDelBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        await indexDb.remove(rec);
        showToast(`deleted 1 pose from "${name}"`);
        await refreshGrid();
      });

      poseItem.appendChild(poseCanvas);
      poseItem.appendChild(poseDelBtn);
      poseGrid.appendChild(poseItem);
    }

    // Add photo button (file upload)
    const addPoseBtn = document.createElement('div');
    addPoseBtn.className = 'pose-item pose-add';
    addPoseBtn.innerHTML = '<div class="pose-add-icon">+</div><div class="pose-add-label">upload</div>';
    addPoseBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.addEventListener('change', async () => {
        if (!input.files?.[0]) return;
        await addPhotoToPerson(name, input.files[0]);
      });
      input.click();
    });
    poseGrid.appendChild(addPoseBtn);

    // Add camera button
    const addCamBtn = document.createElement('div');
    addCamBtn.className = 'pose-item pose-add';
    addCamBtn.innerHTML = '<div class="pose-add-icon">&#x25ef;</div><div class="pose-add-label">camera</div>';
    addCamBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      openQuickCapture(name, gallery);
    });
    poseGrid.appendChild(addCamBtn);

    gallery.appendChild(poseGrid);

    // Toggle gallery on card click
    card.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.contentEditable === 'true') return;
      if (target.closest('.delete-x')) return;
      if (target.closest('.pose-gallery')) return; // don't collapse when interacting with gallery
      card.classList.toggle('expanded');
    });

    const infoRow = document.createElement('div');
    infoRow.className = 'face-card-info';
    infoRow.appendChild(nameDiv);
    infoRow.appendChild(countDiv);
    card.appendChild(canvas); card.appendChild(infoRow); card.appendChild(delBtn);
    card.appendChild(gallery);
    dom.faceGrid.appendChild(card);
  }
}

// ── Add Photo to Existing Person ──

async function addPhotoToPerson(name: string, file: File) {
  if (!file || !file.type.startsWith('image/')) return;
  showToast('detecting face...');

  const url = URL.createObjectURL(file);
  const img = new Image();
  await new Promise<void>(r => { img.onload = () => r(); img.src = url; });

  const result = await human.detect(img);
  const face = result.face[0];
  URL.revokeObjectURL(url);

  if (!face || !face.embedding || face.embedding.length === 0) {
    showToast('no face detected — try another photo');
    return;
  }

  let imageData: ImageData;
  if (face.tensor) {
    const data = await human.tf.browser.toPixels(face.tensor as any);
    imageData = new ImageData(new Uint8ClampedArray(data), face.tensor.shape[1], face.tensor.shape[0]);
    human.tf.dispose(face.tensor);
  } else {
    imageData = new ImageData(1, 1);
  }

  await indexDb.save({ id: 0, name, descriptor: face.embedding, image: imageData });
  showToast(`added photo to "${name}"`);
  await refreshGrid();
}

// ── Quick Capture (inline camera for adding poses) ──

let quickCaptureStream: MediaStream | null = null;

function closeQuickCapture() {
  if (quickCaptureStream) {
    quickCaptureStream.getTracks().forEach(t => t.stop());
    quickCaptureStream = null;
  }
  const existing = document.querySelector('.quick-capture');
  if (existing) existing.remove();
}

async function openQuickCapture(name: string, galleryEl: HTMLElement) {
  closeQuickCapture();

  const container = document.createElement('div');
  container.className = 'quick-capture';

  const video = document.createElement('video');
  video.playsInline = true;
  video.autoplay = true;

  const controls = document.createElement('div');
  controls.className = 'qc-controls';

  const captureBtn = document.createElement('button');
  captureBtn.className = 'qc-capture';
  captureBtn.innerText = 'capture';

  const countLabel = document.createElement('span');
  countLabel.className = 'qc-count';
  countLabel.innerText = '';

  const doneBtn = document.createElement('button');
  doneBtn.className = 'qc-done';
  doneBtn.innerText = 'done';

  controls.appendChild(captureBtn);
  controls.appendChild(countLabel);
  controls.appendChild(doneBtn);
  container.appendChild(video);
  container.appendChild(controls);
  galleryEl.appendChild(container);

  // Start webcam
  try {
    quickCaptureStream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: 'user', width: { ideal: 640 } },
    });
    video.srcObject = quickCaptureStream;
  } catch (e) {
    showToast('camera access denied');
    container.remove();
    return;
  }

  let captured = 0;

  captureBtn.addEventListener('click', async () => {
    captureBtn.disabled = true;
    captureBtn.innerText = 'detecting...';

    const result = await human.detect(video);
    const face = result.face[0];

    if (!face || !face.embedding || face.embedding.length === 0) {
      showToast('no face detected — try again');
      captureBtn.disabled = false;
      captureBtn.innerText = 'capture';
      return;
    }

    let imageData: ImageData;
    if (face.tensor) {
      const data = await human.tf.browser.toPixels(face.tensor as any);
      imageData = new ImageData(new Uint8ClampedArray(data), face.tensor.shape[1], face.tensor.shape[0]);
      human.tf.dispose(face.tensor);
    } else {
      imageData = new ImageData(1, 1);
    }

    await indexDb.save({ id: 0, name, descriptor: face.embedding, image: imageData });
    captured++;
    countLabel.innerText = `${captured} saved`;
    playBeep();

    // Add thumbnail to pose grid
    const poseGrid = galleryEl.querySelector('.pose-grid');
    if (poseGrid) {
      const poseItem = document.createElement('div');
      poseItem.className = 'pose-item';
      const thumb = document.createElement('canvas');
      thumb.width = imageData.width;
      thumb.height = imageData.height;
      thumb.getContext('2d')?.putImageData(imageData, 0, 0);
      poseItem.appendChild(thumb);
      // Insert before the add buttons (last 2 children: upload + camera)
      const addBtns = poseGrid.querySelectorAll('.pose-add');
      if (addBtns.length > 0) poseGrid.insertBefore(poseItem, addBtns[0]);
      else poseGrid.appendChild(poseItem);
    }

    captureBtn.disabled = false;
    captureBtn.innerText = 'capture';
  });

  doneBtn.addEventListener('click', () => {
    closeQuickCapture();
    if (captured > 0) {
      showToast(`added ${captured} pose${captured > 1 ? 's' : ''} to "${name}"`);
      refreshGrid();
    }
  });
}

// ── Detection ──
function stopDetection() {
  detectionActive = false;
  if (detectionThread) cancelAnimationFrame(detectionThread);
  if (drawThread) cancelAnimationFrame(drawThread);
  detectionThread = null; drawThread = null;
  if (dom.captureVideo.srcObject) {
    (dom.captureVideo.srcObject as MediaStream).getTracks().forEach(t => t.stop());
    dom.captureVideo.srcObject = null;
  }
  if (capturedFace?.tensor) { human.tf.dispose(capturedFace.tensor); capturedFace = null; }
}

// ── Guided Enrollment ──
function updateStepUI() {
  const steps = dom.enrollSteps.querySelectorAll('.enroll-step');
  steps.forEach((el, i) => {
    el.classList.remove('active', 'done');
    if (capturedSteps.has(i)) el.classList.add('done');
    else el.classList.add('active'); // all uncaptured are active (any order)
  });
}

function toDeg(rad: number): number { return Math.round(rad * 180 / Math.PI); }

async function openWebcam() {
  closeUpload(); stopDetection();
  capturedSteps.clear(); holdingStep = -1; holdStart = 0;
  capturedPoses.length = 0; logEntries.length = 0;

  dom.methodWebcam.classList.add('active'); dom.methodUpload.classList.remove('active');
  dom.webcamPanel.classList.add('visible'); dom.uploadPanel.classList.remove('visible');
  dom.webcamSaveBtn.disabled = true; dom.webcamName.value = '';

  // Reset thumbs
  dom.poseThumbs.querySelectorAll('.pose-thumb').forEach(el => {
    el.classList.remove('done');
    const c = el.querySelector('canvas') as HTMLCanvasElement;
    c.getContext('2d')?.clearRect(0, 0, c.width, c.height);
  });

  updateStepUI();
  dom.poseIndicator.className = 'pose-indicator waiting';
  dom.poseIndicator.innerText = 'move your head slowly in any direction';
  dom.webcamStatus.className = 'status-msg waiting';
  dom.webcamStatus.innerText = `0 / ${POSE_STEPS.length} poses captured`;

  const stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: 'user', width: { ideal: 640 } } as any });
  const ready = new Promise(r => { dom.captureVideo.onloadeddata = () => r(true); });
  dom.captureVideo.srcObject = stream;
  void dom.captureVideo.play();
  await ready;
  dom.captureCanvas.width = dom.captureVideo.videoWidth;
  dom.captureCanvas.height = dom.captureVideo.videoHeight;

  detectionActive = true;
  enrollDetectLoop();
  enrollDrawLoop();
}

async function enrollDetectLoop() {
  if (!detectionActive) return;
  if (capturedFace?.tensor) human.tf.dispose(capturedFace.tensor);
  await human.detect(dom.captureVideo);
  capturedFace = human.result.face[0] || null;

  if (capturedFace && capturedSteps.size < POSE_STEPS.length) {
    const face = capturedFace;
    const yawDeg = toDeg(face.rotation?.angle?.yaw || 0);
    const pitchDeg = toDeg(face.rotation?.angle?.pitch || 0);
    const rollDeg = toDeg(face.rotation?.angle?.roll || 0);
    const gazeStr = face.rotation?.gaze?.strength || 0;
    const boxSize = Math.min(face.box[2], face.box[3]);

    const baseValid = human.result.face.length === 1
      && boxSize > 120
      && (face.faceScore || 0) > 0.5
      && (face.embedding || []).length > 0;

    // Find which uncaptured pose matches current head position
    let matchedStep = -1;
    if (baseValid) {
      for (let i = 0; i < POSE_STEPS.length; i++) {
        if (capturedSteps.has(i)) continue;
        const step = POSE_STEPS[i];
        if (yawDeg >= step.yawMin && yawDeg <= step.yawMax
          && pitchDeg >= step.pitchMin && pitchDeg <= step.pitchMax) {
          matchedStep = i;
          break;
        }
      }
    }

    const currentLabel = matchedStep >= 0 ? POSE_STEPS[matchedStep].label : '—';

    // Log every frame
    logEntries.push({
      t: Math.round(human.now()),
      step: currentLabel,
      yaw: yawDeg, pitch: pitchDeg, roll: rollDeg,
      gaze: Math.round(gazeStr * 100),
      score: Math.round((face.faceScore || 0) * 100),
      real: Math.round((face.real || 0) * 100),
      live: Math.round((face.live || 0) * 100),
      size: Math.round(boxSize),
      holding: matchedStep >= 0 && holdStart > 0,
      holdMs: holdStart > 0 ? Math.round(human.now() - holdStart) : 0,
    });

    if (matchedStep >= 0) {
      // If we switched to a different pose, reset hold timer
      if (holdingStep !== matchedStep) { holdStart = 0; holdingStep = matchedStep; }
      if (holdStart === 0) holdStart = human.now();
      const held = human.now() - holdStart;

      dom.poseIndicator.className = 'pose-indicator holding';
      dom.poseIndicator.innerText = `${POSE_STEPS[matchedStep].label} — hold... ${Math.round((HOLD_TIME - held) / 100) / 10}s`;

      if (held >= HOLD_TIME) {
        // Capture this pose
        const step = POSE_STEPS[matchedStep];
        const tensor = face.tensor;
        let imageData: ImageData;
        if (tensor) {
          const data = await human.tf.browser.toPixels(tensor as any);
          imageData = new ImageData(new Uint8ClampedArray(data), tensor.shape[1], tensor.shape[0]);
        } else {
          imageData = new ImageData(1, 1);
        }

        capturedPoses.push({
          embedding: face.embedding || [],
          imageData,
          yaw: yawDeg, pitch: pitchDeg, roll: rollDeg,
          faceScore: face.faceScore || 0,
          real: face.real || 0,
          live: face.live || 0,
          age: face.age || 0,
          gender: face.gender || '?',
          genderScore: face.genderScore || 0,
        });

        // Update thumbnail
        const thumbEl = dom.poseThumbs.querySelector(`[data-step="${matchedStep}"]`);
        if (thumbEl && tensor) {
          thumbEl.classList.add('done');
          const thumbCanvas = thumbEl.querySelector('canvas') as HTMLCanvasElement;
          try { await human.tf.browser.toPixels(tensor as any, thumbCanvas); } catch (_e) { /* ok */ }
        }

        capturedSteps.add(matchedStep);
        holdStart = 0;
        holdingStep = -1;
        updateStepUI();
        playBeep();

        dom.poseIndicator.className = 'pose-indicator captured';
        dom.poseIndicator.innerText = `${step.label} captured!`;
        dom.webcamStatus.innerText = `${capturedSteps.size} / ${POSE_STEPS.length} poses captured`;

        if (capturedSteps.size >= POSE_STEPS.length) {
          dom.webcamSaveBtn.disabled = false;
          dom.webcamStatus.className = 'status-msg ready';
          dom.webcamStatus.innerText = `all ${POSE_STEPS.length} poses captured — enter a name and save`;
          dom.poseIndicator.className = 'pose-indicator captured';
          dom.poseIndicator.innerText = 'enrollment complete!';
        } else {
          // Brief flash before resuming
          await new Promise(r => setTimeout(r, 400));
          dom.poseIndicator.className = 'pose-indicator waiting';
          dom.poseIndicator.innerText = 'move your head slowly in any direction';
        }
      }
    } else {
      holdStart = 0;
      holdingStep = -1;
      if (capturedSteps.size < POSE_STEPS.length) {
        dom.poseIndicator.className = 'pose-indicator waiting';
        dom.poseIndicator.innerText = 'move your head slowly in any direction';
      }
    }

    // Update validation sidebar
    renderLiveData(face, yawDeg, pitchDeg, rollDeg, gazeStr);
  }

  if (detectionActive) detectionThread = requestAnimationFrame(enrollDetectLoop);
}

// ── Mirror Utilities ──

function mirrorResults(result: any, width: number) {
  const m = JSON.parse(JSON.stringify(result));
  for (const face of m.face || []) {
    if (face.box) face.box[0] = width - face.box[0] - face.box[2];
    if (face.boxRaw) face.boxRaw[0] = 1 - face.boxRaw[0] - face.boxRaw[2];
    if (face.mesh) for (const p of face.mesh) p[0] = width - p[0];
    if (face.meshRaw) for (const p of face.meshRaw) p[0] = 1 - p[0];
    if (face.annotations) for (const k of Object.keys(face.annotations)) for (const p of face.annotations[k]) p[0] = width - p[0];
    if (face.rotation?.angle) { face.rotation.angle.yaw = -face.rotation.angle.yaw; face.rotation.angle.roll = -face.rotation.angle.roll; }
    if (face.rotation?.gaze) { face.rotation.gaze.bearing = -face.rotation.gaze.bearing; }
  }
  return m;
}

async function enrollDrawLoop() {
  if (!detectionActive && capturedSteps.size >= POSE_STEPS.length) return;
  if (!dom.captureVideo.paused) {
    const interpolated = human.next(human.result);
    // Draw mirrored video frame
    const ctx = dom.captureCanvas.getContext('2d');
    if (ctx) {
      ctx.save();
      ctx.translate(dom.captureCanvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(dom.captureVideo, 0, 0, dom.captureCanvas.width, dom.captureCanvas.height);
      ctx.restore();
    }
    // Mirror detection coordinates to match mirrored video, then draw
    const mirrored = mirrorResults(interpolated, dom.captureCanvas.width);
    await human.draw.all(dom.captureCanvas, mirrored);
  }
  drawThread = requestAnimationFrame(enrollDrawLoop);
}

function renderLiveData(face: H.FaceResult, yaw: number, pitch: number, roll: number, gaze: number) {
  // Check if current pose matches any uncaptured step
  let matchingAny = false;
  for (let i = 0; i < POSE_STEPS.length; i++) {
    if (capturedSteps.has(i)) continue;
    const s = POSE_STEPS[i];
    if (yaw >= s.yawMin && yaw <= s.yawMax && pitch >= s.pitchMin && pitch <= s.pitchMax) { matchingAny = true; break; }
  }

  const data: Record<string, { status: boolean, val: string }> = {
    captured: { status: capturedSteps.size >= POSE_STEPS.length, val: `${capturedSteps.size}/${POSE_STEPS.length}` },
    yaw: { status: matchingAny, val: `${yaw}°` },
    pitch: { status: matchingAny, val: `${pitch}°` },
    roll: { status: Math.abs(roll) < 20, val: `${roll}°` },
    gaze: { status: gaze < 0.5, val: `${Math.round(gaze * 100)}` },
    confidence: { status: (face.faceScore || 0) > 0.5, val: `${Math.round((face.faceScore || 0) * 100)}` },
    size: { status: Math.min(face.box[2], face.box[3]) > 120, val: `${Math.round(Math.min(face.box[2], face.box[3]))}` },
    real: { status: (face.real || 0) > 0.3, val: `${Math.round((face.real || 0) * 100)}` },
    live: { status: (face.live || 0) > 0.5, val: `${Math.round((face.live || 0) * 100)}` },
    descriptor: { status: (face.embedding || []).length > 0, val: `${(face.embedding || []).length}` },
  };

  dom.validationStatus.innerHTML = '';
  for (const [key, val] of Object.entries(data)) {
    const item = document.createElement('div');
    item.className = `v-item ${val.status ? 'pass' : 'fail'}`;
    item.innerHTML = `<span class="v-label">${key}</span><span class="v-val">${val.val}</span>`;
    dom.validationStatus.appendChild(item);
  }
}

async function saveEnrollment() {
  const name = dom.webcamName.value.trim();
  if (!name) { dom.webcamName.focus(); return; }
  if (capturedPoses.length === 0) return;

  for (const pose of capturedPoses) {
    await indexDb.save({ id: 0, name, descriptor: pose.embedding, image: pose.imageData });
  }
  showToast(`saved "${name}" with ${capturedPoses.length} poses`);
  closeWebcam();

  // If opened via guided enrollment (?mode=register), redirect back to main page
  const params = new URLSearchParams(window.location.search);
  if (params.get('mode') === 'register') {
    setTimeout(() => { window.location.href = 'index.html'; }, 800);
    return;
  }
  await refreshGrid();
}

function copyLogs() {
  const summary = {
    poses: capturedPoses.map((p, i) => ({
      step: POSE_STEPS[i]?.label || `pose${i}`,
      yaw: p.yaw, pitch: p.pitch, roll: p.roll,
      faceScore: Math.round(p.faceScore * 100),
      real: Math.round(p.real * 100),
      live: Math.round(p.live * 100),
      age: Math.round(p.age),
      gender: `${p.gender} ${Math.round(p.genderScore * 100)}%`,
      embeddingLength: p.embedding.length,
    })),
    frameLog: logEntries.slice(-200), // last 200 frames
    totalFrames: logEntries.length,
  };
  const text = JSON.stringify(summary, null, 2);
  navigator.clipboard.writeText(text).then(() => showToast('logs copied to clipboard'));
}

function closeWebcam() {
  stopDetection();
  dom.webcamPanel.classList.remove('visible');
  dom.methodWebcam.classList.remove('active');
}

// ── Upload ──
async function openUploadPanel() {
  closeWebcam();
  dom.methodUpload.classList.add('active'); dom.methodWebcam.classList.remove('active');
  dom.uploadPanel.classList.add('visible'); dom.webcamPanel.classList.remove('visible');
  dom.uploadSaveBtn.disabled = true; dom.uploadName.value = '';
  dom.uploadImage.style.display = 'none';
  dom.uploadArea.classList.remove('has-image');
  dom.uploadValidation.innerHTML = ''; uploadFace = null;
  dom.uploadStatus.className = 'status-msg waiting';
  dom.uploadStatus.innerText = 'upload a photo with a clear face';
}

function closeUpload() {
  dom.uploadPanel.classList.remove('visible');
  dom.methodUpload.classList.remove('active');
  if (uploadFace?.tensor) { human.tf.dispose(uploadFace.tensor); uploadFace = null; }
}

async function handleFileSelect(file: File) {
  if (!file || !file.type.startsWith('image/')) return;
  dom.uploadStatus.className = 'status-msg waiting';
  dom.uploadStatus.innerText = 'detecting face...';
  dom.uploadSaveBtn.disabled = true;

  const url = URL.createObjectURL(file);
  const img = dom.uploadImage;
  await new Promise<void>(r => { img.onload = () => r(); img.src = url; });

  if (uploadFace?.tensor) human.tf.dispose(uploadFace.tensor);
  const result = await human.detect(img);
  const face = result.face[0];

  if (!face || !face.embedding || face.embedding.length === 0) {
    dom.uploadStatus.className = 'status-msg error';
    dom.uploadStatus.innerText = 'no face detected — try another photo';
    dom.uploadValidation.innerHTML = '<div class="v-item fail"><span class="v-label">face</span><span class="v-val">not found</span></div>';
    URL.revokeObjectURL(url); return;
  }

  // Crop to face
  const pad = 0.4;
  const [bx, by, bw, bh] = face.box;
  const padX = bw * pad, padY = bh * pad;
  const sx = Math.max(0, bx - padX), sy = Math.max(0, by - padY);
  const sw = Math.min(img.naturalWidth - sx, bw + padX * 2);
  const sh = Math.min(img.naturalHeight - sy, bh + padY * 2);
  const canvasW = 400, canvasH = Math.round(canvasW * (sh / sw));
  dom.uploadCanvas.width = canvasW; dom.uploadCanvas.height = canvasH;
  const ctx = dom.uploadCanvas.getContext('2d')!;
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvasW, canvasH);

  const scale = canvasW / sw;
  const offsetResult = JSON.parse(JSON.stringify(result));
  for (const f of offsetResult.face) {
    f.box = [(f.box[0] - sx) * scale, (f.box[1] - sy) * scale, f.box[2] * scale, f.box[3] * scale];
    if (f.mesh) f.mesh = f.mesh.map((p: number[]) => [(p[0] - sx) * scale, (p[1] - sy) * scale, p[2] || 0]);
    if (f.annotations) { for (const k of Object.keys(f.annotations)) { f.annotations[k] = f.annotations[k].map((p: number[]) => [(p[0] - sx) * scale, (p[1] - sy) * scale, p[2] || 0]); } }
  }
  await human.draw.face(dom.uploadCanvas, offsetResult.face);

  dom.uploadArea.classList.add('has-image');
  const placeholder = dom.uploadArea.querySelector('.upload-placeholder') as HTMLElement;
  if (placeholder) placeholder.style.display = 'none';
  uploadFace = face;

  const uploadOk: Record<string, { status: boolean, val: number }> = {
    faceCount: { status: result.face.length === 1, val: result.face.length },
    faceConfidence: { status: (face.faceScore || 0) > 0.5, val: Math.round((face.faceScore || 0) * 100) },
    faceSize: { status: Math.min(face.box[2], face.box[3]) > 80, val: Math.round(Math.min(face.box[2], face.box[3])) },
    antispoof: { status: (face.real || 0) > 0.3, val: Math.round((face.real || 0) * 100) },
    descriptor: { status: face.embedding.length > 0, val: face.embedding.length },
  };
  renderValidation(dom.uploadValidation, uploadOk);

  const valid = uploadOk.faceCount.status && uploadOk.faceConfidence.status && uploadOk.descriptor.status;
  dom.uploadSaveBtn.disabled = !valid;
  dom.uploadStatus.className = valid ? 'status-msg ready' : 'status-msg error';
  dom.uploadStatus.innerText = valid ? 'face detected — enter a name and save'
    : result.face.length > 1 ? 'multiple faces — use a photo with one face' : 'low quality — try another photo';
  URL.revokeObjectURL(url);
}

function renderValidation(container: HTMLDivElement, data: Record<string, { status: boolean, val: number }>) {
  container.innerHTML = '';
  for (const [key, val] of Object.entries(data)) {
    const item = document.createElement('div');
    item.className = `v-item ${val.status ? 'pass' : 'fail'}`;
    item.innerHTML = `<span class="v-label">${key}</span><span class="v-val">${val.val || (val.status ? 'ok' : 'fail')}</span>`;
    container.appendChild(item);
  }
}

async function saveUploadFace() {
  const name = dom.uploadName.value.trim();
  if (!name) { dom.uploadName.focus(); return; }
  if (!uploadFace?.tensor || !uploadFace?.embedding) return;
  const tensor = uploadFace.tensor;
  const data = await human.tf.browser.toPixels(tensor as any);
  const imageData = new ImageData(new Uint8ClampedArray(data), tensor.shape[1], tensor.shape[0]);
  await indexDb.save({ id: 0, name, descriptor: uploadFace.embedding, image: imageData });
  showToast(`saved "${name}"`);
  closeUpload();
  await refreshGrid();
}

// ── Delete All ──
async function deleteAll() {
  const faces = await indexDb.load();
  for (const f of faces) await indexDb.remove(f);
  showToast(`deleted ${faces.length} face(s)`);
  dom.confirmOverlay.classList.remove('visible');
  await refreshGrid();
}

// ── Init ──
async function init() {
  console.log('manage: initializing...');
  renderStationBadge(dom.stationBadge);
  await human.load();
  await human.warmup();
  console.log('manage: ready');
  dom.loadingOverlay.classList.add('hidden');

  dom.methodWebcam.addEventListener('click', openWebcam);
  dom.methodUpload.addEventListener('click', () => { openUploadPanel(); dom.fileInput.click(); });
  dom.webcamSaveBtn.addEventListener('click', saveEnrollment);
  dom.webcamCancelBtn.addEventListener('click', closeWebcam);
  dom.retakeBtn.addEventListener('click', () => { if (detectionActive) openWebcam(); });
  dom.uploadSaveBtn.addEventListener('click', saveUploadFace);
  dom.uploadCancelBtn.addEventListener('click', closeUpload);

  dom.uploadArea.addEventListener('click', () => dom.fileInput.click());
  dom.fileInput.addEventListener('change', () => { if (dom.fileInput.files?.[0]) handleFileSelect(dom.fileInput.files[0]); });
  dom.uploadArea.addEventListener('dragover', (e) => { e.preventDefault(); dom.uploadArea.style.borderColor = 'royalblue'; });
  dom.uploadArea.addEventListener('dragleave', () => { dom.uploadArea.style.borderColor = ''; });
  dom.uploadArea.addEventListener('drop', (e) => {
    e.preventDefault(); dom.uploadArea.style.borderColor = '';
    if (e.dataTransfer?.files?.[0]) { if (!dom.uploadPanel.classList.contains('visible')) openUploadPanel(); handleFileSelect(e.dataTransfer.files[0]); }
  });

  dom.deleteAllBtn.addEventListener('click', () => dom.confirmOverlay.classList.add('visible'));
  dom.confirmDeleteAll.addEventListener('click', deleteAll);
  dom.confirmCancel.addEventListener('click', () => dom.confirmOverlay.classList.remove('visible'));

  const mode = new URLSearchParams(window.location.search).get('mode');
  if (mode === 'register') openWebcam();
  await refreshGrid();
}

window.onload = init;
